import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { axiosInstance } from "@/lib/api-client"
import { API_ROUTES } from "@/lib/api-routes"
import { filenameFromDisposition } from "@/lib/download"

import type {
  BillingOverview,
  Invoice,
  InvoiceFilters,
  InvoicesPage,
  OrderQuote,
  OrderQuoteRequest,
  PayQuote,
  TransactionFilters,
  TransactionsPage,
  Wallet,
} from "../types"

export const PAGE_SIZE = 50
export const DOCUMENTS_PAGE_SIZE = 20

// ── Overview (billing home) ─────────────────────────────────────────
export function billingOverviewQueryOptions() {
  return queryOptions({
    queryKey: ["billing", "overview"],
    queryFn: async () => {
      const res = await axiosInstance.get<BillingOverview>(
        API_ROUTES.BILLING.OVERVIEW
      )
      return res.data
    },
  })
}

export function useBillingOverviewQuery() {
  return useQuery(billingOverviewQueryOptions())
}

// ── Wallet ──────────────────────────────────────────────────────────
export function walletQueryOptions() {
  return queryOptions({
    queryKey: ["billing", "wallet"],
    queryFn: async () => {
      const res = await axiosInstance.get<Wallet>(API_ROUTES.BILLING.WALLET)
      return res.data
    },
  })
}

export function useWalletQuery(enabled = true) {
  return useQuery({ ...walletQueryOptions(), enabled })
}

// ── Transactions (paginated, filterable) ────────────────────────────
export function transactionsQueryOptions(filters: TransactionFilters = {}) {
  const { module, type, skip = 0, limit = PAGE_SIZE } = filters
  return queryOptions({
    queryKey: [
      "billing",
      "transactions",
      { module: module ?? null, type: type ?? null, skip, limit },
    ],
    queryFn: async () => {
      const res = await axiosInstance.get<TransactionsPage>(
        API_ROUTES.BILLING.TRANSACTIONS,
        {
          params: {
            ...(module ? { module } : {}),
            ...(type ? { type } : {}),
            skip,
            limit,
          },
        }
      )
      return res.data
    },
    // Hold the current page while the next one loads (no empty flash).
    placeholderData: keepPreviousData,
  })
}

export function useTransactionsQuery(filters: TransactionFilters = {}) {
  return useQuery(transactionsQueryOptions(filters))
}

// ── Statement (the ledger, page by page) ────────────────────────────
export const STATEMENT_PAGE_SIZE = 100

/**
 * The ledger for the statement, newest first, loaded a page at a time as
 * the customer asks for earlier months. `module` narrows it to one
 * service.
 */
export function statementQueryOptions(module?: string) {
  return infiniteQueryOptions({
    queryKey: ["billing", "statement", { module: module ?? null }],
    queryFn: async ({ pageParam }) => {
      const res = await axiosInstance.get<TransactionsPage>(
        API_ROUTES.BILLING.TRANSACTIONS,
        {
          params: {
            ...(module ? { module } : {}),
            skip: pageParam,
            limit: STATEMENT_PAGE_SIZE,
          },
        }
      )
      return res.data
    },
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.skip + last.items.length
      return next < last.total && last.items.length > 0 ? next : undefined
    },
    placeholderData: keepPreviousData,
  })
}

export function useStatementQuery(module?: string) {
  return useInfiniteQuery(statementQueryOptions(module))
}

/**
 * Enough recent ledger (every service) to draw the last 30 days of the
 * balance and total this month's top-ups.
 */
export const RECENT_TRANSACTIONS = { limit: 200 } as const

// ── Order quote ─────────────────────────────────────────────────────
/**
 * What ordering these servers costs, priced server-side from their plans:
 * the prorated charge now, the full monthly total, the lane capacity it
 * results in, and any shortfall. A POST, but read-only — a query.
 */
export function orderQuoteQueryOptions(order: OrderQuoteRequest) {
  return queryOptions({
    queryKey: ["billing", "order-quote", order],
    queryFn: async () => {
      const res = await axiosInstance.post<OrderQuote>(
        API_ROUTES.BILLING.ORDER_QUOTE,
        order
      )
      return res.data
    },
    // Days left + balance shift under us — never reuse an old quote.
    staleTime: 0,
    // Changing the order keeps the last quote up while the new one loads.
    placeholderData: keepPreviousData,
  })
}

export function useOrderQuoteQuery(order: OrderQuoteRequest) {
  return useQuery({
    ...orderQuoteQueryOptions(order),
    enabled: order.servers.length > 0,
  })
}

// ── Documents (invoices, credit notes, receipts, debit notes) ───────
export function invoicesQueryOptions(filters: InvoiceFilters = {}) {
  const {
    status,
    documents,
    module,
    skip = 0,
    limit = DOCUMENTS_PAGE_SIZE,
  } = filters
  return queryOptions({
    queryKey: [
      "billing",
      "invoices",
      "list",
      {
        status: status ?? null,
        documents: documents ?? null,
        module: module ?? null,
        skip,
        limit,
      },
    ],
    queryFn: async () => {
      // Repeated `document=` keys — axios would otherwise send
      // `document[]=`. The server maps each type to its kinds.
      const params = new URLSearchParams()
      if (status) params.set("status", status)
      for (const doc of documents ?? []) params.append("document", doc)
      if (module) params.set("module", module)
      params.set("skip", String(skip))
      params.set("limit", String(limit))
      const res = await axiosInstance.get<InvoicesPage>(
        API_ROUTES.BILLING.INVOICES,
        { params }
      )
      return res.data
    },
    placeholderData: keepPreviousData,
  })
}

export function useInvoicesQuery(filters: InvoiceFilters = {}) {
  return useQuery(invoicesQueryOptions(filters))
}

/** One document by id — the detail view. */
export function invoiceQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["billing", "invoices", "detail", id],
    queryFn: async () => {
      const res = await axiosInstance.get<Invoice>(
        API_ROUTES.BILLING.INVOICE(id)
      )
      return res.data
    },
  })
}

export function useInvoiceQuery(id: string, enabled = true) {
  return useQuery({
    ...invoiceQueryOptions(id),
    enabled: enabled && Boolean(id),
  })
}

/**
 * What paying this invoice early would charge right now — re-priced the
 * same way the payment is — and whether the balance covers it. Never
 * reused from cache: servers and balance move under it.
 */
export function payQuoteQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["billing", "invoices", "pay-quote", id],
    queryFn: async () => {
      const res = await axiosInstance.get<PayQuote>(
        API_ROUTES.BILLING.INVOICE_PAY_QUOTE(id)
      )
      return res.data
    },
    staleTime: 0,
    // The payment still works (and is still checked) without a quote.
    retry: false,
  })
}

export function usePayQuoteQuery(id: string, enabled = true) {
  return useQuery({
    ...payQuoteQueryOptions(id),
    enabled: enabled && Boolean(id),
  })
}

/**
 * Pay the open monthly invoice early. The backend refreshes the amount to
 * the customer's current servers at payment time, so the returned invoice
 * replaces the cached row. Settles (success or not) refresh the whole
 * billing tree — a 409 means the list we held is already out of date.
 */
export function usePayInvoiceMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosInstance.post<Invoice>(
        API_ROUTES.BILLING.INVOICE_PAY(id)
      )
      return res.data
    },
    onSuccess: (paid) => {
      qc.setQueriesData<InvoicesPage>(
        { queryKey: ["billing", "invoices", "list"] },
        (page) =>
          page && {
            ...page,
            items: page.items.map((i) => (i.id === paid.id ? paid : i)),
          }
      )
      qc.setQueryData(invoiceQueryOptions(paid.id).queryKey, paid)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["billing"] })
    },
  })
}

// ── Document PDF ────────────────────────────────────────────────────
/**
 * Fetch a document's PDF as a blob with the session cookie (`inline` asks
 * for a viewable rather than attachment disposition), along with the
 * filename the server named it. A mutation, not a query, and `no-store`:
 * the status printed on the PDF changes when an invoice is paid, so it is
 * refetched every time and never cached.
 */
export function useInvoicePdfMutation() {
  return useMutation({
    mutationFn: async ({
      id,
      inline = false,
    }: {
      id: string
      inline?: boolean
    }) => {
      const res = await axiosInstance.get<Blob>(
        API_ROUTES.BILLING.INVOICE_PDF(id),
        {
          responseType: "blob",
          params: inline ? { inline: true } : undefined,
          adapter: "fetch",
          fetchOptions: { cache: "no-store" },
        }
      )
      return {
        blob: res.data,
        // null when the server named none; the caller has the fallback.
        filename:
          filenameFromDisposition(res.headers["content-disposition"], "") ||
          null,
      }
    },
  })
}
